import { ref } from "vue";

const SOUND_PREFERENCE_KEY = "lifeworth-realtime-alert-sound";
const SOUND_COOLDOWN_MS = 2500;
const MAX_REMEMBERED_EVENTS = 100;
const soundEnabled = ref(true);
const playedEvents = new Set();
let audioContext = null;
let audioUnavailable = false;
let lastPlayedAt = 0;
let removeUnlockListeners = null;

try {
  soundEnabled.value = window.localStorage.getItem(SOUND_PREFERENCE_KEY) !== "off";
} catch {
  // Sound remains enabled when browser storage is unavailable.
}

const contextForSound = () => {
  if (audioContext) return audioContext;
  if (audioUnavailable) return null;

  const AudioContext = window.AudioContext || window.webkitAudioContext;
  if (!AudioContext) return null;

  try {
    audioContext = new AudioContext();
    return audioContext;
  } catch {
    audioUnavailable = true;
    return null;
  }
};

const rememberEvent = eventId => {
  if (!eventId) return;

  playedEvents.add(String(eventId));
  if (playedEvents.size > MAX_REMEMBERED_EVENTS) {
    playedEvents.delete(playedEvents.values().next().value);
  }
};

const playTone = (context, frequency, startsAt, duration, volume) => {
  const oscillator = context.createOscillator();
  const gain = context.createGain();

  oscillator.type = "sine";
  oscillator.frequency.setValueAtTime(frequency, startsAt);
  gain.gain.setValueAtTime(0.0001, startsAt);
  gain.gain.exponentialRampToValueAtTime(volume, startsAt + 0.018);
  gain.gain.exponentialRampToValueAtTime(0.0001, startsAt + duration);

  oscillator.connect(gain);
  gain.connect(context.destination);
  oscillator.start(startsAt);
  oscillator.stop(startsAt + duration + 0.02);
};

const playChime = (context, event) => {
  const startsAt = context.currentTime + 0.025;
  const isMessage = event?.action === "message_added" || Boolean(event?.message_id);

  playTone(context, isMessage ? 784 : 659, startsAt, 0.16, 0.11);
  playTone(context, isMessage ? 1047 : 880, startsAt + 0.12, 0.22, 0.09);
};

export const initializeRealtimeAlertSound = () => {
  if (removeUnlockListeners) return removeUnlockListeners;

  const unlock = () => {
    const context = contextForSound();
    if (!context) {
      removeUnlockListeners?.();
      return;
    }

    const resumed = context.state === "suspended" ? context.resume() : Promise.resolve();
    resumed.then(() => {
      if (context.state === "running") removeUnlockListeners?.();
    }).catch(() => {
      // A later user interaction can try to unlock sound again.
    });
  };

  const remove = () => {
    window.removeEventListener("pointerdown", unlock, true);
    window.removeEventListener("keydown", unlock, true);
    removeUnlockListeners = null;
  };

  removeUnlockListeners = remove;
  window.addEventListener("pointerdown", unlock, { capture: true, passive: true });
  window.addEventListener("keydown", unlock, true);

  return remove;
};

export const playRealtimeAlertSound = event => {
  const eventId = event?.event_id;
  if (!soundEnabled.value || (eventId && playedEvents.has(String(eventId)))) {
    return false;
  }

  rememberEvent(eventId);

  const now = Date.now();
  if (now - lastPlayedAt < SOUND_COOLDOWN_MS) return false;

  const context = contextForSound();
  if (!context || context.state !== "running") return false;

  try {
    playChime(context, event);
    lastPlayedAt = now;
    return true;
  } catch {
    return false;
  }
};

export const setRealtimeAlertSoundEnabled = enabled => {
  soundEnabled.value = Boolean(enabled);

  try {
    window.localStorage.setItem(SOUND_PREFERENCE_KEY, soundEnabled.value ? "on" : "off");
  } catch {
    // The in-memory preference still applies for this session.
  }

  if (soundEnabled.value) {
    const context = contextForSound();
    if (context?.state === "suspended") {
      context.resume().catch(() => {});
    }
  }
};

export const realtimeAlertSoundEnabled = soundEnabled;
